import { useMutation } from "@tanstack/react-query";
import { privateApi } from "../../../api/api";
import { ValidationErrorResponse } from "../response/ValidationErrorResponse";
import { UpdatePropertyType } from "../schema/UpdatedPropertySchema";
import { UpdatePropertyResponse } from "../response/UpdatePropertyResponse";

export const useUpdateProperty = () => {
    return useMutation({
        mutationFn: async (values: {
            propertyId: string;
            formData: UpdatePropertyType;
        }) => {
            const safeAppend = (key: string, value: any) => {
                if (value !== undefined) {
                    newFormData.append(key, value === null ? "" : value);
                }
            };

            const newFormData = new FormData();

            // Required Fields
            newFormData.append("property_type", values.formData.property_type);
            newFormData.append("name", values.formData.name);
            newFormData.append("preferred_currency", values.formData.preferred_currency);
            newFormData.append("time_zone", values.formData.time_zone);
            newFormData.append("primary_email", values.formData.primary_email);

            // Optional Fields
            // if (values.formData.primary_phone_country_code) newFormData.append("primary_phone_country_code", values.formData.primary_phone_country_code);
            // if (values.formData.primary_phone) newFormData.append("primary_phone", values.formData.primary_phone);
            // if (values.formData.postal_code) newFormData.append("postal_code", values.formData.postal_code);
            // if (values.formData.country) newFormData.append("country", values.formData.country);
            // if (values.formData.state) newFormData.append("state", values.formData.state);
            // if (values.formData.city) newFormData.append("city", values.formData.city);
            // if (values.formData.street_address) newFormData.append("street_address", values.formData.street_address);
            // if (values.formData.longitude) newFormData.append("longitude", values.formData.longitude);
            // if (values.formData.latitude) newFormData.append("latitude", values.formData.latitude);
            // if (values.formData.description) newFormData.append("description", values.formData.description);
            // if (values.formData.website) newFormData.append("website", values.formData.website);
            // Gunakan safeAppend untuk setiap field:
            safeAppend("primary_phone_country_code", values.formData.primary_phone_country_code);
            safeAppend("primary_phone", values.formData.primary_phone);
            safeAppend("postal_code", values.formData.postal_code);
            safeAppend("country", values.formData.country);
            safeAppend("state", values.formData.state);
            safeAppend("city", values.formData.city);
            safeAppend("street_address", values.formData.street_address);
            safeAppend("longitude", values.formData.longitude);
            safeAppend("latitude", values.formData.latitude);
            safeAppend("description", values.formData.description);
            safeAppend("website", values.formData.website);
            safeAppend("email_autofill", values.formData.email_autofill);
            safeAppend("term_condition", values.formData.term_condition);
            safeAppend("cancellation_policy", values.formData.cancellation_policy);
            safeAppend("region_id", values.formData.region_id);
            // group_id sengaja SELALU dikirim (bukan lewat safeAppend yang skip saat
            // undefined) kalau field-nya ada di form values, termasuk saat user
            // memilih "No Group" (null) — backend membaca ini sebagai "lepas dari
            // semua group", beda dengan field tidak dikirim sama sekali yang berarti
            // "jangan ubah assignment group yang sudah ada".
            if (values.formData.group_id !== undefined) {
                newFormData.append("group_id", values.formData.group_id === null ? "" : values.formData.group_id);
            }

            const { format, parse } = await import("date-fns");
            const formatTime = (timeStr: string | null | undefined) => {
                if (!timeStr) return null;
                try {
                    return format(parse(timeStr, "HH:mm", new Date()), "hh:mm a").toLowerCase();
                } catch (e) {
                    return null;
                }
            };

            const checkinStart = formatTime(values.formData.checkin_time_start);
            const checkinEnd = formatTime(values.formData.checkin_time_end);
            if (checkinStart && checkinEnd) {
                safeAppend("checkin_time", `${checkinStart} - ${checkinEnd}`);
            } else if (checkinStart) {
                safeAppend("checkin_time", checkinStart);
            }

            const checkoutStart = formatTime(values.formData.checkout_time_start);
            const checkoutEnd = formatTime(values.formData.checkout_time_end);
            if (checkoutStart && checkoutEnd) {
                safeAppend("checkout_time", `${checkoutStart} - ${checkoutEnd}`);
            } else if (checkoutStart) {
                safeAppend("checkout_time", checkoutStart);
            }

            // Handle array of emails
            if (values.formData.cc_emails) {
                values.formData.cc_emails.forEach((email, index) => {
                    newFormData.append(`cc_emails[${index}]`, email);
                });
            }

            if (values.formData.accounting_email) {
                newFormData.append("accounting_email", values.formData.accounting_email);
            }

            if (values.formData.mobile_number_country_code) {
                newFormData.append("mobile_number_country_code", values.formData.mobile_number_country_code);
            }

            if (values.formData.mobile_number) {
                newFormData.append("mobile_number", values.formData.mobile_number);
            }

            if (values.formData.enable_cvv !== null) {
                newFormData.append("enable_cvv", String(values.formData.enable_cvv));
            }

            // Handle Logo (File Upload)
            if (values.formData.logo instanceof File) {
                newFormData.append("logo", values.formData.logo);
            }

            newFormData.append("_method", "PUT");

            return await privateApi.post(`/v4/property/${values.propertyId}`, newFormData, {
                headers: { "Content-Type": "multipart/form-data" },
            })
        },
        onSuccess: async (response) => {
            try {
                const validationResult = UpdatePropertyResponse.safeParse(response.data);

                if (!validationResult.success) {
                    console.error("Validation failed:", validationResult.error.errors);
                    throw new Error("Invalid update property data format");
                }

                return validationResult.data;
            } catch (error) {
                console.error('Failed to update property', error);
                throw error;
            }
        },
        onError: (error) => {
            const validationResult = ValidationErrorResponse.safeParse((error as any).response.data);

            if (!validationResult.success) {
                console.error("Validation failed:", validationResult.error.errors);
                throw new Error("Invalid property data format");
            } else {
                console.error('Failed to update property', error);
                throw error;
            }
        },
    });
};